import { computed } from 'vue'
import navigationRoutes, { type INavigationRoute } from '../components/sidebar/NavigationRoutes'
import { useAuthStore } from '../stores/auth'
import { useSettingsStore } from '../stores/settings'

// The role/mode-filtered navigation tree — the single source the sidebar (desktop) and the
// mobile "More" sheet both render, so the two can never drift. Mirrors the filtering that
// AppSidebar performs.

function filterRoutesByRole(routes: INavigationRoute[], role?: string): INavigationRoute[] {
  return routes
    .map((route) => {
      const roles = route.meta?.roles
      const allowedByRole = !roles || roles.length === 0 || (role ? roles.includes(role) : false)
      if (!allowedByRole) return null
      if (!route.children?.length) return route
      const children = filterRoutesByRole(route.children, role)
      if (children.length === 0) return null
      return { ...route, children }
    })
    .filter((route): route is INavigationRoute => route !== null)
}

// Streets and Buildings are mutually exclusive per community mode.
function filterRoutesByMode(routes: INavigationRoute[], mode: string): INavigationRoute[] {
  return routes
    .map((route) => {
      if (route.name === 'streets' && mode === 'CONDOMINIUM') return null
      if (route.name === 'buildings' && mode !== 'CONDOMINIUM') return null
      if (!route.children?.length) return route
      const children = filterRoutesByMode(route.children, mode)
      if (children.length === 0) return null
      return { ...route, children }
    })
    .filter((route): route is INavigationRoute => route !== null)
}

export function useNavTree() {
  const authStore = useAuthStore()
  const settingsStore = useSettingsStore()

  const allowedRoutes = computed(() => {
    const role = authStore.user?.role
    const mode = settingsStore.data?.communityType ?? 'SUBDIVISION'
    return filterRoutesByMode(filterRoutesByRole(navigationRoutes.routes, role), mode)
  })

  // Community-plan features stay visible with a lock badge; the router guard redirects a
  // locked tenant to the Upgrade page on tap.
  const isLocked = (route: INavigationRoute) =>
    route.meta?.requiresPlan === 'COMMUNITY' && !authStore.featuresUnlocked

  return { allowedRoutes, isLocked }
}
